import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useRegisterSync } from "./hooks/useRegisterSync";
import { useStoreSwitchReload } from "./hooks/useStoreSwitchReload";
import { getCategories, getProductsByCategory, searchProducts, findByCode, getTaxMap, getBogos, buildMixMatchMap, mixMatchFor, resolveTaxList, isProductCategoryEnabled, categoryLoyaltyFlags, getCoupons } from "./data/queries";
import { deriveRestoredAltriaFlags } from "./engine/itemCoupon";
import { setBogoList, setCouponExcludedCategories, addItem, loadCart, resetCart, setAgeVerified, addPayoutLine, setDualPrice } from "../../Redux/features/WebRegister/cartSlice";
import { dualPriceConfig } from "./engine/dualPrice";
import HeldOrdersModal from "./modals/HeldOrdersModal";
import { getHeldOrders, addHeldOrder, removeHeldOrder, snapshotCart } from "./data/heldOrders";
import { generateOrderId } from "./engine/savePayload";
import { useNumericKeys } from "./hooks/useNumericKeys";
import { useOfflineOrderQueue } from "./hooks/useOfflineOrderQueue";
import { reconcileInFlight } from "./data/inFlight";
import { listActiveOrders, activeOrderHasCharges } from "./data/activeOrder";
import { getRegisterSettings, getEnableOrderNumber, deziGateOn } from "./data/registerSettings";
import { useDeziDiscounts } from "./hooks/useDeziDiscounts";
import QuickAddProductModal, { waitForQuickAddedProduct } from "./modals/QuickAddProductModal";
import RecoveryModal from "./modals/RecoveryModal";
import CameraScanModal from "./modals/CameraScanModal";
import { unlockScanAudio } from "./data/scanFeedback";
import IssueGiftCardModal from "./modals/IssueGiftCardModal";
import ReceiptModal from "./modals/ReceiptModal";
import PrinterSettingsModal from "./modals/PrinterSettingsModal";
import AgeVerifyModal from "./modals/AgeVerifyModal";
import { validOrderDiscount, validCoupon, validOtdOpen, validLottery } from "./validators";
import { canRegister } from "./permissions";
import { useAuthDetails } from "../../Common/cookiesHelper";
import PermissionDeniedEmptyState from "../../reuseableComponents/PermissionDeniedEmptyState";
import { ToastifyAlert } from "../../CommonComponents/ToastifyAlert";
import SearchScanBar from "./layout/SearchScanBar";
import CategoryGrid from "./layout/CategoryGrid";
import ProductGrid from "./layout/ProductGrid";
import MultiplierBar from "./layout/MultiplierBar";
import MultiplierColumn from "./layout/MultiplierColumn";
import Keypad from "./layout/Keypad";
import AmountDisplay from "./layout/AmountDisplay";
import ActionColumn from "./layout/ActionColumn";
import CartPanel from "./layout/CartPanel";
import CheckoutBar from "./layout/CheckoutBar";
import "./register.css";
import ActionBar from "./layout/ActionBar";
import VariantModal from "./modals/VariantModal";
import PaymentMethodModal from "./modals/PaymentMethodModal";
import CustomDiscountModal from "./modals/CustomDiscountModal";
import CouponModal from "./modals/CouponModal";
import OtdModal from "./modals/OtdModal";
import StockPromptModal from "./modals/StockPromptModal";
import HoldBasketModal from "./modals/HoldBasketModal";
import CashOpModal from "./modals/CashOpModal";
import VendorPayoutModal from "./modals/VendorPayoutModal";
import PayoutKindModal from "./modals/PayoutKindModal";
import ConfirmModal from "./modals/ConfirmModal";
import CustomItemTaxModal from "./modals/CustomItemTaxModal";
import { kickDrawer } from "./data/cashDrawer";
import LineActionsModal from "./modals/LineActionsModal";
import OrdersModal from "./modals/OrdersModal";
import CustomerModal from "./modals/CustomerModal";
import CustomQtyModal from "./modals/CustomQtyModal";
import QtyInfoModal from "./modals/QtyInfoModal";
import { buildAddPayload, parseVariants, stockOf, applyAmountKey, amountFromBuffer, androidCustomLineName } from "./uiUtils";
import { phpQtyById } from "./data/api";
import { useThemeController, RegThemeContext, ThemeToggle } from "./theme";

// All register layout/visuals now live in `register.css`, scoped under `.qv-reg`. Inline styles
// cannot express media queries, which is precisely why this screen could not adapt to a phone or a
// tablet before; the design system owns layout, and `useRegTheme` remains only for the handful of
// modals that still compute colours in JS.

export default function WebRegister() {
  const dispatch = useDispatch();
  const store = useStore();
  const { mode, toggle, tokens } = useThemeController();
  useStoreSwitchReload(); // reload + re-sync if the store is switched in another tab while open
  const sync = useRegisterSync();
  // Merchant/superadmin bypass all permission checks; employees gated on WEBRG.
  const allowed = canRegister("MENU_WEB_REGISTER");
  const { LoginGetDashBoardRecordJson } = useAuthDetails();
  const storeName = LoginGetDashBoardRecordJson?.data?.name || "";

  const [taxMap, setTaxMap] = useState(new Map());
  const [mixMap, setMixMap] = useState(new Map()); // Mix&Match deals keyed "pid|vid" / "pid|"
  const [regSettings, setRegSettings] = useState(null); // surcharge/NCA + cashback config
  const [categories, setCategories] = useState([]);
  // Android onProductClicked reads orderCreateViewModel.categoryList (the live register list) at
  // tap time; a ref keeps addProduct's stable callback reading the CURRENT list, not a stale closure.
  const categoriesRef = useRef(categories);
  categoriesRef.current = categories;
  const [products, setProducts] = useState([]);
  const [view, setView] = useState("category"); // "category" | "product"
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [variantProduct, setVariantProduct] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [showHeld, setShowHeld] = useState(false);
  const [showOrders, setShowOrders] = useState(false); // order history + refunds
  const [showPrinters, setShowPrinters] = useState(false);
  const [heldCount, setHeldCount] = useState(() => getHeldOrders().length);
  const { pending: pendingSync } = useOfflineOrderQueue(); // background flush of queued orders
  const [receipt, setReceipt] = useState(null); // post-payment receipt model (print/email/sms)
  const [recovery, setRecovery] = useState(null); // unfinished order awaiting resume/discard
  const [resumeOrderId, setResumeOrderId] = useState(null); // id of a resumed order (kept consistent)
  const [modal, setModal] = useState(null); // 'custom' | 'discount' | 'coupon' | 'otd'
  const [lineAction, setLineAction] = useState(null);
  const [multiplier, setMultiplier] = useState(1); // selectedProductQuantity (2x..11x / Custom)
  const [showCustomQty, setShowCustomQty] = useState(false);
  const [customQty, setCustomQty] = useState(null); // Android "Custom (N)" label; cleared whenever the multiplier resets to 1
  useEffect(() => {
    if (Number(multiplier) === 1) setCustomQty(null);
  }, [multiplier]);
  const [holdOpen, setHoldOpen] = useState(false); // POS hold-basket dialog (basket name + mobile)
  const [cashOp, setCashOp] = useState(null); // { kind: 'drop'|'payin'|'vendor', amount } — POS ⋮-menu cash operations (action-column buttons on web)
  const [payoutPick, setPayoutPick] = useState(null); // keyed amount awaiting "Lottery Payout" / "Scratcher Payout" (Android lotteryAndScratcherPayoutDialog)
  const [customItemFlow, setCustomItemFlow] = useState(null); // Custom Item flow: { kind: 'tax'|'ebt', amount } (Android tvCustomItem branches)
  const [quickPayAmount, setQuickPayAmount] = useState(0); // NUMPAD_PAYOUT: keyed amount handed to the payment screen
  const [qtyInfo, setQtyInfo] = useState(null); // long-press "Current Quantity" popup
  const [amountBuffer, setAmountBuffer] = useState(""); // keypad dollar-amount pad (cents), feeds Custom Item / OTD
  const [deziWaiting, setDeziWaiting] = useState(false); // blocking overlay while a lookup settles
  const [quickAdd, setQuickAdd] = useState(null); // { name, upc } — Quick-Add prefill, null = closed
  const [notFoundPrompt, setNotFoundPrompt] = useState(null); // barcode_msg 3/5: { name, upc } awaiting Yes/No
  const [syncConfirm, setSyncConfirm] = useState(false); // Android manual Sync confirm
  const [manualSyncing, setManualSyncing] = useState(false);
  const [searchClear, setSearchClear] = useState(0); // bump to clear the product-search box (POS clears it on category open)
  // Small-screen chrome. Both are inert on desktop (the CSS never shows their controls there), so
  // the wide layout behaves exactly as before regardless of these values.
  const [cartOpen, setCartOpen] = useState(false); // cart bottom-sheet
  const [toolsOpen, setToolsOpen] = useState(false); // keypad + actions drawer
  const [showCamScan, setShowCamScan] = useState(false); // live camera barcode scanner

  // QV-7124 — manufacturer (Altria/mKonnekt) discounts. Fully gated: on a store without the flags the
  // hook never fires a request and every downstream gate is a no-op, so non-dezi carts are unchanged.
  //
  // The customer's phone is part of the lookup because mKonnekt can return CUSTOMER-SPECIFIC
  // (loyalty-funded) discounts on top of the product ones — attaching a customer must re-ask.
  const cartCustomer = useSelector((s) => s.webRegisterCart.customer);
  const deziOn = deziGateOn(regSettings);
  const dezi = useDeziDiscounts({
    enabled: deziOn,
    rcn: regSettings ? regSettings.rcn : "",
    customerPhone: (cartCustomer && (cartCustomer.phone || cartCustomer.mobile)) || "",
  });

  const onAmountKey = useCallback((k) => setAmountBuffer((b) => applyAmountKey(b, k)), []);
  // Physical-keyboard support for the home amount pad — active only on the home view with no modal
  // open (a modal's own keypad takes over, so digits never feed two buffers at once).
  const anyModalOpen = !!variantProduct || showPayment || showHeld || !!modal || !!lineAction || showCustomQty || !!qtyInfo || holdOpen || !!cashOp || payoutPick != null || !!customItemFlow;
  useNumericKeys(onAmountKey, { enabled: view === "category" && !anyModalOpen });

  // On load, recover an unfinished payment. First: a persisted in-progress order with a real charge
  // (mid-split crash) → offer Resume/Discard via RecoveryModal (full recovery). Only if there's no
  // Surcharge / cashback config — loaded once (rarely changes); drives NCA + debit cashback.
  useEffect(() => {
    getRegisterSettings()
      .then((s) => {
        setRegSettings(s);
        dispatch(setDualPrice(dualPriceConfig(s))); // QV-4241: reprices the cart in dual-price mode
      })
      .catch(() => {});
  }, [dispatch]);

  // CLOSE/RELOAD GUARD (task #37): warn before leaving when (a) paid sales are still syncing to the
  // server (local-first queue — closing the tab pauses sync until the register reopens), or (b) a
  // payment is mid-flight with tenders already applied (real charges could orphan). Suppressed for
  // the PROGRAMMATIC store-switch reload — blocking that would strand the register on a stale store.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (window.__qvProgrammaticReload) return;
      const paying = (store.getState().webRegisterCart.payments || []).length > 0;
      if (pendingSync > 0 || paying) {
        e.preventDefault();
        e.returnValue = ""; // browsers show their generic "Leave site?" prompt
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [pendingSync, store]);

  // such record do we fall back to reconciling stray in-flight markers and alerting about an orphan.
  useEffect(() => {
    (async () => {
      const actives = await listActiveOrders();
      const unfinished = actives.find(activeOrderHasCharges);
      if (unfinished) {
        setRecovery(unfinished);
        return;
      }
      const recovered = await reconcileInFlight().catch(() => []);
      recovered.forEach((r) =>
        ToastifyAlert(`A ${r.cardBrand || "card"} charge (order ${r.terminalOrderId}) went through but its order was not completed — please verify/refund.`, "error")
      );
    })();
  }, []);
  // Permission gate for the action buttons (owners bypass via canRegister); denial → toast.
  const gated = (menuKey, fn) => () => {
    if (menuKey && !canRegister(menuKey)) return ToastifyAlert("Access denied", "error");
    fn();
  };
  const cartState = () => store.getState().webRegisterCart;

  // QV-7124 — hold the cashier back only while a manufacturer-discount lookup that MATTERS to this
  // cart is in flight. Both gates are deliberately scoped by `waitWarranted()` rather than "a request
  // is running": at a dezi store almost every sale is ordinary products, and gating on the raw
  // in-flight flag made every one of them show a spinner for a discount that was never coming.
  const openLineEditor = (l) => {
    if (dezi.waitWarranted())
      return ToastifyAlert("Manufacturer discount is still loading, please try again.", "error");
    setLineAction(l);
  };
  // Waiting before the payment screen is MONEY, not polish: the payment screen reads the totals live
  // and the tender saves what it reads, so proceeding mid-lookup charges the pre-discount total.
  const openPayment = async () => {
    if (dezi.waitWarranted()) {
      setDeziWaiting(true);
      try {
        await dezi.awaitSettled(); // bounded — a hung lookup can never wedge the register
      } finally {
        setDeziWaiting(false);
      }
      if (cartState().lines.length === 0) return; // cart was cleared while we waited
    }
    // Android proceedToPayment: with NUMPAD_PAYOUT on, a keyed numpad amount becomes the amount to
    // pay on the payment screen (quickPayViewModel.setAmount), then the numpad is cleared.
    const keyed = amountFromBuffer(amountBuffer);
    setQuickPayAmount(regSettings && regSettings.numpadPayout && keyed > 0 ? keyed : 0);
    if (keyed > 0) setAmountBuffer("");
    setShowPayment(true);
  };
  // Run an Android validator result: on failure show its exact message, else proceed.
  const guard = (res, fn) => {
    if (!res.ok) return ToastifyAlert(res.message, "error");
    fn();
  };

  // Load reference data once the sync is ready, and re-load after each poll (sync.tick) so category
  // order/visibility, taxes, and promos update in place without a page refresh.
  useEffect(() => {
    if (!sync.ready) return;
    (async () => {
      // Android register list (~8548): lottery categories are listed only when the store's `ebt`
      // setting has option "4" — the same list that isCategoryEnable checks products against.
      const lotteryEnabled = !!(regSettings && Array.isArray(regSettings.ebtList) && regSettings.ebtList.includes("4"));
      const [cats, tm, bogos, mm] = await Promise.all([getCategories({ lotteryEnabled }), getTaxMap(), getBogos(), buildMixMatchMap()]);
      setCategories(cats);
      setTaxMap(tm);
      setMixMap(mm);
      dispatch(setBogoList(bogos));
      // exclude_coupons_store: register categories flagged exclude-coupons → their products never
      // receive an ORDER-level list-coupon share (Android per-line isCouponEligible).
      dispatch(setCouponExcludedCategories(cats.filter((c) => String(c.exclude_coupons_store) === "1").map((c) => String(c.id))));
      // Warm the enable_order_number cache at register open (Android keeps it in local store details)
      // so the LOCAL-FIRST checkout builds its payload instantly, never waiting on this API.
      getEnableOrderNumber().catch(() => {});
    })();
  }, [sync.ready, sync.tick, dispatch, regSettings]);

  // Grid tiles and scan both pass a specific variant (or null for non-variant). Stock-tracked items
  // are capped like the POS: can only add up to `qty` units total (requested + already in cart).
  // Mirrors the POS: the multiplier (selectedProductQuantity) resets to 1 after EVERY add attempt.
  const addProduct = useCallback(
    (product, variant, qty = 1) => {
      try {
        // Android onProductClicked guards that sit in FRONT of the add: block while an OTD price is
        // active (it would corrupt the back-solve), and reject a disabled variant.
        // Android onProductClicked order: product disabled → category disabled → OTD → stock → 21+.
        if (String(product.disable) === "1") return ToastifyAlert("Product is disabled from inventory", "error");
        if (!product.isCustomLineItem && !isProductCategoryEnabled(product, categoriesRef.current))
          return ToastifyAlert("Product's category is disable from inventory.", "error");
        if (cartState().isOutdoor) return ToastifyAlert("Remove the OTD price first", "error");
        if (variant && String(variant.disable) === "1") return ToastifyAlert("This item is unavailable", "error");
        const { tracked, qty: stockQty } = stockOf(product, variant);
        const vId = variant ? variant.variantId : "";
        const inCart = store
          .getState()
          .webRegisterCart.lines.filter(
            (l) => l.orderProductId === String(product.id) && (l.variantId || "") === (vId || "")
          )
          .reduce((sum, l) => sum + (Number(l.orderCount) || 0), 0);
        if (tracked && stockQty < qty + inCart) {
          // Android getShouldAddToCart == false: qty 0 → outOfStockDialog (STOCK_PROMPT register
          // setting ⇒ "update quantity" prompt, else the limit message); qty > 0 → the limit message
          // with the current quantity.
          if (stockQty <= 0) {
            if (regSettingsRef.current && regSettingsRef.current.stockPrompt) setStockPrompt({ product, variant, qty });
            else ToastifyAlert("Quantity limit exceeded. The requested quantity exceeds the available stock.", "error");
          } else {
            ToastifyAlert(`Current Quantity = ${stockQty}. Quantity limit exceeded. The requested quantity exceeds the available stock.`, "error");
          }
          return;
        }
        // 21+/age gate: a tobacco item needs age verification (once per order) before it can be added.
        const isTobacco = String(variant ? variant.isTobacco : product.is_tobacco) === "1";
        if (isTobacco && !cartState().isEighteenPlus) {
          setAgePending({ product, variant, qty });
          return;
        }
        // Per-transaction purchase limit (validatePurchaseQty; 0 = unlimited).
        const pCap = Number(variant ? variant.purchaseQty : product.purchase_qty) || 0;
        if (pCap > 0 && qty + inCart > pCap) {
          ToastifyAlert(`This product is limited to ${pCap} per transaction`, "error");
          return;
        }
        // Android: a line carries its category-derived earn/use-point eligibility (loyalty award +
        // redemption honour the per-category flags).
        const loyFlags = categoryLoyaltyFlags(product, categoriesRef.current);
        dispatch(addItem({ ...buildAddPayload(product, variant, taxMap, qty, mixMatchFor(mixMap, product, variant)), ...loyFlags }));
      } finally {
        setMultiplier(1); // POS resetQuantity(): revert to 1 after every add (even a blocked one)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, taxMap, mixMap, store]
  );
  const [agePending, setAgePending] = useState(null); // { product, variant, qty } awaiting 21+ verify
  const [stockPrompt, setStockPrompt] = useState(null); // { product, variant, qty } awaiting the stock-prompt quantity update
  const regSettingsRef = useRef(regSettings);
  regSettingsRef.current = regSettings;

  // Long-press a tile → fetch the live quantity from the server (POS get_Qty_ById) and show it.
  const onLongPress = useCallback(async (cell) => {
    const label = cell.label;
    setQtyInfo({ label, loading: true, quantity: null, error: null });
    try {
      const res = await phpQtyById({ pid: cell.product.id, vid: cell.variant ? cell.variant.variantId : "" });
      const q = res?.data?.quantity;
      setQtyInfo({ label, loading: false, quantity: q == null ? "—" : q, error: null });
    } catch (e) {
      setQtyInfo({ label, loading: false, quantity: null, error: "Couldn't fetch quantity" });
    }
  }, []);

  const onSelectCategory = useCallback(async (cat) => {
    setSearchClear((n) => n + 1); // POS clears the product-search box when a category is opened
    setView("product");
    setLoadingProducts(true);
    try {
      setProducts(await getProductsByCategory(cat.id));
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  const onSearch = useCallback(async (text) => {
    if (!text) {
      setView("category");
      setProducts([]);
      return;
    }
    setView("product");
    setLoadingProducts(true);
    try {
      // Android CombinationDao.searchByTitle (QV-7607): search results drop variant rows with no valid
      // variant id — category browsing keeps them. Marked here, filtered by ProductGrid.
      setProducts((await searchProducts(text)).map((p) => ({ ...p, __fromSearch: true })));
    } finally {
      setLoadingProducts(false);
    }
  }, []);

  const onScan = useCallback(
    async (code) => {
      const found = await findByCode(code);
      if (!found) {
        // Android IPF:8994-9090 — the store's barcode_msg setting decides: 1 "Product Not Found",
        // 2/4 open Quick Add at once, 3/5 ask "Would you like to add it now?" first.
        const mode = String((regSettingsRef.current && regSettingsRef.current.barcodeMsg) || "1");
        const prefill = { name: "", upc: String(code) };
        if (mode === "2" || mode === "4") {
          if (canRegister("MENU_QUICKADD")) setQuickAdd(prefill);
          else ToastifyAlert("Access Denied!", "error");
        } else if (mode === "3" || mode === "5") {
          setNotFoundPrompt(prefill);
        } else {
          ToastifyAlert("Product Not Found", "error");
        }
        return;
      }
      const variants = parseVariants(found);
      if (variants.length === 0) {
        addProduct(found, null); // non-variant → add directly
        return;
      }
      const lc = String(code).toLowerCase();
      const match =
        variants.find((v) => String(v.upc).toLowerCase() === lc) ||
        variants.find((v) => (v.customCodes || []).some((c) => c.toLowerCase() === lc));
      if (match) addProduct(found, match); // scanned a specific variant → add it directly (no picker)
      else setVariantProduct(found); // ambiguous (matched product-level) → let the user pick (rare)
    },
    [addProduct]
  );

  // Camera scan → the SAME pipeline as a typed/wedge scan. `candidates` is the decoded text plus its
  // UPC-A⇄EAN-13 sibling (see normalizeScanCandidates): the catalog is probed with the same
  // findByCode the scan input uses, and the first form that RESOLVES is handed to onScan — so the
  // add, variant match, 21+ gate and multiplier consumption are byte-identical to a keyboard scan.
  // When nothing resolves, onScan(raw) reproduces the standard miss (toast + Quick Add prefilled).
  const onCameraCode = useCallback(
    async (candidates) => {
      for (const cand of candidates) {
        if (await findByCode(cand)) {
          onScan(cand);
          return { ok: true, code: cand };
        }
      }
      onScan(candidates[0]);
      return { ok: false, code: candidates[0] };
    },
    [onScan]
  );

  // Camera detection pauses while ANY dialog sits above it (they all render at higher z), so a scan
  // needing a decision — variant picker, 21+ verify, unknown-code Quick Add — can't be re-triggered
  // by the barcode still under the lens while the cashier answers. (Declared here, after every state
  // it reads — agePending in particular is declared above, not at the top of the component.)
  const camPaused =
    anyModalOpen || !!agePending || !!stockPrompt || !!quickAdd || showOrders || showPrinters || !!recovery || !!receipt || deziWaiting;

  // Hold Sale / Held — park the current pre-payment cart locally and resume it later.
  // Android setupPopupMenu handlers (Vendor Payout / Drop Cash / Pay In): internet → no split in
  // progress → permission → a keyed amount > 0 ("Enter Amount") → open the dialog, kick the drawer
  // (openOnlyCashDrawer), clear the keypad amount.
  const onCashOp = (kind, permission) => {
    if (typeof navigator !== "undefined" && navigator.onLine === false) return ToastifyAlert("No internet connection", "error");
    if ((cartState().payments || []).length > 0) return ToastifyAlert("Split payment in progress", "error");
    if (!canRegister(permission)) return ToastifyAlert("Access denied", "error");
    const amt = amountFromBuffer(amountBuffer);
    if (amt <= 0) return ToastifyAlert("Enter Amount", "error");
    setCashOp({ kind, amount: amt });
    try {
      kickDrawer(regSettings);
    } catch {
      /* drawer is best-effort */
    }
    setAmountBuffer("");
  };

  // Android "Lottery/Scratcher Payout" menu item: internet → no split in progress → validLottery
  // (amount keyed, no gift card, no OTD) → the two-button dialog → a payout CART LINE; keypad cleared.
  const onLotteryPayout = () => {
    if (typeof navigator !== "undefined" && navigator.onLine === false) return ToastifyAlert("No internet connection", "error");
    if ((cartState().payments || []).length > 0) return ToastifyAlert("Split payment in progress", "error");
    const amt = amountFromBuffer(amountBuffer);
    const v = validLottery(cartState(), amt);
    if (!v.ok) return ToastifyAlert(v.message, "error");
    setPayoutPick(amt);
  };
  const pickPayout = (kind) => {
    const amt = payoutPick;
    setPayoutPick(null);
    if (!(amt > 0)) return;
    dispatch(addPayoutLine({ kind, amount: amt }));
    setAmountBuffer("");
  };

  // Android setOpenHoldBasketDialog → the cashier names the basket (prefilled from the customer)
  // before the sale is parked; the Held list shows that name.
  const onHoldSale = () => {
    const cart = store.getState().webRegisterCart;
    // Android isSplitShowError: nothing can be parked once a split tender has been taken.
    if ((cart.payments || []).length > 0) return ToastifyAlert("Split payment in progress", "error");
    if (!cart.lines.length) return ToastifyAlert("Cart is empty", "error");
    setHoldOpen(true);
  };
  const holdSale = ({ basketName, mobile }) => {
    const cart = store.getState().webRegisterCart;
    if (!cart.lines.length) return ToastifyAlert("Cart is empty", "error");
    const itemCount = cart.lines.reduce((n, l) => n + (Number(l.orderCount) || 0), 0);
    addHeldOrder({
      id: generateOrderId(),
      name: basketName,
      mobile,
      at: new Date().toISOString(),
      itemCount,
      total: cart.totals.grandTotal,
      snapshot: snapshotCart(cart),
    });
    dispatch(resetCart());
    setHeldCount(getHeldOrders().length);
    setHoldOpen(false);
    ToastifyAlert("Sale held", "success");
  };
  // Every cart that comes back from storage goes through here. Android (InStoreRestoreViewModel)
  // never persists an item coupon's Altria-funded flag — it re-reads it from the local coupons table
  // while rebuilding the cart, so a coupon whose switch was turned off (or that was deleted) restores
  // as "0". The web mirrors that instead of trusting its own snapshot; coupons unavailable → all "0".
  const restoreSnapshot = async (snapshot) => {
    const snap = snapshot || {};
    let coupons = [];
    try {
      coupons = await getCoupons();
    } catch {
      /* IDB unavailable — every flag falls back to "0", exactly like Android's not-found path */
    }
    const lines = Array.isArray(snap.lines) ? deriveRestoredAltriaFlags(snap.lines, coupons) : snap.lines;
    dispatch(loadCart({ ...snap, lines }));
  };
  const onResumeHeld = async (o) => {
    if (store.getState().webRegisterCart.lines.length) return ToastifyAlert("Hold the current sale first", "error"); // POS: one open basket at a time
    await restoreSnapshot(o.snapshot);
    removeHeldOrder(o.id);
    setHeldCount(getHeldOrders().length);
    setShowHeld(false);
  };

  // Default tax bucket for a plain custom item (mirrors the POS default-tax path).
  const defaultTax = () => {
    for (const t of taxMap.values()) {
      if (t.title === "DefaultTax") return [{ id: t.id, title: t.title, displayName: t.displayname || null, percent: t.percent }];
    }
    return [];
  };

  // Add a custom line at `price`, auto-numbering by base name → "Custom Item 1/2/3", "Vapes 1/2..".
  // Custom lines never dedup (cartSlice.addItem pushes each isCustomLineItem separately). Clears the
  // keypad amount + multiplier afterwards (POS clearItemAmount + resetQuantity).
  // Android customItemWithAndWithoutTax / addCategoryAsItem → addToCart(model): the model carries
  // foodStampable (EBT), isTobacco (CUSTOM_ITEM_CHECK_ID) and, for a category-as-item, the category
  // id + title (naming counts per category). The keypad multiplier applies like a catalog product and
  // EACH unit is named by isCustomItem(i) against the cart as it grows. An age-restricted custom item
  // goes through the 21+ gate first (showAgeRestrictedForCustomItem) unless the order is verified.
  const addCustomLine = (baseName, price, taxList, opts = {}) => {
    const { categoryId = "", categoryName = "", foodStampable = false, isTobacco = false } = opts;
    if (isTobacco && !cartState().isEighteenPlus) {
      setAgePending({ custom: [baseName, price, taxList, { ...opts }] });
      return;
    }
    const units = Math.max(1, Number(multiplier) || 1);
    // Android: a category-as-item earns/uses points per its category's flags; a plain custom item
    // (no category) earns/uses nothing.
    const loyFlags = categoryId ? categoryLoyaltyFlags({ cotegory: categoryId }, categoriesRef.current) : { canEarnPoint: false, canUsePoint: false };
    for (let i = 0; i < units; i++) {
      const lines = store.getState().webRegisterCart.lines;
      const name = androidCustomLineName(lines, baseName, categoryId);
      dispatch(
        addItem({
          productId: "0",
          name,
          unitPrice: price,
          isCustomLineItem: true,
          categoryId: categoryId || undefined,
          canEarnPoint: loyFlags.canEarnPoint,
          canUsePoint: loyFlags.canUsePoint,
          taxList: taxList || [],
          model: {
            title: name,
            price,
            categoryName: categoryName || "",
            cotegory: categoryId || "",
            foodStampable: foodStampable ? "1" : "0",
            isTobacco: isTobacco ? "1" : "0",
          },
          qty: 1,
        })
      );
    }
    setAmountBuffer("");
    setMultiplier(1);
  };

  // Android tvCustomItem: permission → amount keyed → no OTD → then, by register settings:
  //   CUSTOM_CATEGORY off  → tax-picker dialog (taxes + "Allow EBT" when ALLOW_CUSTOM_ITEM_EBT)
  //   CUSTOM_CATEGORY on   → ALLOW_CUSTOM_ITEM_EBT ? "Sell Custom Item As Food Stampable" prompt : add
  //   the item takes the DEFAULT tax in the second case (defaultTaxId).
  const onCustomItem = () => {
    if (cartState().isOutdoor) return ToastifyAlert("Remove the OTD price first", "error");
    const amt = amountFromBuffer(amountBuffer);
    if (amt <= 0) return ToastifyAlert("Enter Amount", "error");
    const rs = regSettings || {};
    if (!rs.customCategoryTax) return setCustomItemFlow({ kind: "tax", amount: amt });
    if (rs.allowCustomItemEbt) return setCustomItemFlow({ kind: "ebt", amount: amt });
    addCustomLine("Custom Item", amt, defaultTax(), { isTobacco: !!rs.customItemCheckId });
  };

  // Tap a category: with a keyed amount → add it as a numbered custom item (POS category-as-item,
  // taxed by the category's own taxes); otherwise drill into the category's products.
  const onCategoryTap = (cat) => {
    const amt = amountFromBuffer(amountBuffer);
    if (amt <= 0) return onSelectCategory(cat);
    // Android onCategoryClicked guards for category-as-item: OTD active, lottery category, permission.
    if (cartState().isOutdoor) return ToastifyAlert("Remove the OTD price first", "error");
    if (String(cat.is_lottery) === "1") return ToastifyAlert("Lottery can't be added as a category item", "error");
    if (!canRegister("MENU_CUSTOM_ITEM")) return ToastifyAlert("Access denied", "error");
    // Android addCategoryAsItem: title = category title, cotegory = id, otherTaxes = category.tax,
    // foodStampable = category.is_ebt_eligible, isTobacco = CUSTOM_ITEM_CHECK_ID (no EBT prompt here).
    const title = cat.title || cat.cat_name || "Item";
    addCustomLine(title, amt, resolveTaxList({ other_taxes: cat.tax }, taxMap), {
      categoryId: String(cat.id),
      categoryName: title,
      foodStampable: String(cat.is_ebt_eligible) === "1",
      isTobacco: !!(regSettings && regSettings.customItemCheckId),
    });
  };

  if (!allowed) return <PermissionDeniedEmptyState />;

  if (!sync.ready) {
    return (
      <div className="qv-reg" data-theme={mode}>
        <div className="qv-boot">
          {sync.error ? (
            <>
              <div className="qv-boot-title" style={{ color: "var(--danger)" }}>Couldn't load the register catalog.</div>
              <div className="qv-boot-sub">{String(sync.error?.message || sync.error)}</div>
            </>
          ) : (
            <>
              <div className="qv-boot-mark" aria-hidden="true">▮▯▮</div>
              <div className="qv-boot-title">Preparing the register</div>
              <div className="qv-boot-sub">Syncing inventory — <span className="qv-num">{sync.progress}%</span></div>
              <div className="qv-boot-bar"><span style={{ width: `${sync.progress}%` }} /></div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <RegThemeContext.Provider value={tokens}>
    <div className="qv-reg" data-theme={mode}>
      <div className="qv-top">
        <span className="qv-store">
          {storeName || "Sale Register"}
          <small>Sale Register</small>
        </span>
        <div className="qv-top-actions">
          {pendingSync > 0 && (
            <span className="qv-sync" title="Orders saved locally, syncing to the server">↻ <span className="qv-num">{pendingSync}</span></span>
          )}
          <button
            className="qv-btn qv-btn--ghost"
            disabled={manualSyncing}
            onClick={() => {
              // Android home ⋮ → Sync (QV-7471): offline → already running → confirm → full re-sync.
              if (typeof navigator !== "undefined" && navigator.onLine === false) return ToastifyAlert("Not connected to Internet", "error");
              if (manualSyncing) return ToastifyAlert("Sync is already in progress. Please wait…", "error");
              setSyncConfirm(true);
            }}
            title="Re-sync the catalog"
          >
            {manualSyncing ? `Syncing ${Math.round(sync.progress || 0)}%` : "Sync"}
          </button>
          <button
            className="qv-btn qv-btn--ghost"
            onClick={() => {
              // Android onViewReceiptsAction (IPF:2530): online + MENU_RECEIPTS before the receipts list.
              if (typeof navigator !== "undefined" && navigator.onLine === false) return ToastifyAlert("No Internet", "error");
              if (!canRegister("MENU_RECEIPTS")) return ToastifyAlert("Access Denied!", "error");
              setShowOrders(true);
            }}
            title="Order history & refunds"
          >
            <span aria-hidden="true">◱</span><span className="qv-top-label">Orders</span>
          </button>
          <button className="qv-btn qv-btn--ghost" onClick={() => setShowHeld(true)} title="Held sales">
            <span aria-hidden="true">❙❙</span><span className="qv-top-label">Held{heldCount > 0 ? ` (${heldCount})` : ""}</span>
          </button>
          <button className="qv-btn qv-btn--ghost" onClick={() => setShowPrinters(true)} title="Printer & cash drawer settings">
            <span aria-hidden="true">⎙</span><span className="qv-top-label">Printers</span>
          </button>
          <button className="qv-btn qv-btn--primary" onClick={onHoldSale} title="Hold this sale">
            <span aria-hidden="true">⏸</span><span className="qv-top-label">Hold Sale</span>
          </button>
          <ThemeToggle mode={mode} onToggle={toggle} />
        </div>
      </div>

      <div className="qv-body">
      <div className="qv-work">
        <SearchScanBar
          onSearch={onSearch}
          onScan={onScan}
          onCamera={() => {
            // iOS unlocks web audio only when a source STARTS during a tap — this tap. Without it
            // the scan chirp is silently blocked (real-iPhone report, 2026-08-17).
            unlockScanAudio();
            setShowCamScan(true);
          }}
          onHome={() => setView("category")}
          view={view}
          clearSignal={searchClear}
        />
        {view === "category" ? (
          <div className="qv-home">
            {/* Tools toggle — only rendered on small screens (CSS), where the keypad/actions block
                would otherwise bury the catalogue. */}
            <div className="qv-toolsbar">
              <button
                className={toolsOpen ? "qv-btn is-on" : "qv-btn"}
                aria-pressed={toolsOpen}
                aria-expanded={toolsOpen}
                onClick={() => setToolsOpen((v) => { if (!v) setCartOpen(false); return !v; })}
              >
                ⌗ Keypad &amp; actions
              </button>
            </div>
            <div className="qv-panel qv-cats-wrap qv-scroll">
              <CategoryGrid categories={categories} onSelect={onCategoryTap} />
            </div>
            <div className={toolsOpen ? "qv-panel qv-pad is-open" : "qv-panel qv-pad"}>
              {/* Sheet header — small screens only (CSS). The pad is a bottom sheet there, and a
                  sheet needs a stated title and an explicit way out; the scrim alone is a guess. */}
              <div className="qv-pad-head">
                <span className="qv-pad-head-title">Keypad &amp; actions</span>
                <button className="qv-btn qv-btn--ghost" onClick={() => setToolsOpen(false)}>Done</button>
              </div>
              <AmountDisplay buffer={amountBuffer} />
              <div className="qv-pad-row">
                {/* Entry group: how many, then which digits. */}
                <div className="qv-pad-entry">
                  <MultiplierColumn value={multiplier} customQty={customQty} onSelect={(n) => { setCustomQty(null); setMultiplier(n); }} onCustom={() => setShowCustomQty(true)} />
                  <Keypad onKey={onAmountKey} />
                </div>
                {/* Order-level actions — a separate group because they act on the SALE, not the
                    amount. Keeping them visually apart stops a mis-tap turning a keypad entry into
                    a discount. */}
                <div className="qv-pad-acts">
                  <ActionColumn
                    onCustomItem={gated("MENU_CUSTOM_ITEM", onCustomItem)}
                    onQuickAdd={gated("MENU_QUICKADD", () => setQuickAdd({ name: "", upc: "" }))}
                    onDiscount={gated("MENU_DISCOUNT", () => guard(validOrderDiscount(cartState()), () => setModal("discount")))}
                    onCoupon={gated("MENU_APPLY_COUPONS", () => guard(validCoupon(cartState()), () => setModal("coupon")))}
                    onOtd={gated("MENU_OTD_PRICE", () => guard(validOtdOpen(cartState()), () => setModal("otd")))}
                    onIssueGiftCard={gated("MENU_GIFT_CARD_ISSUE_REDEEM", () => {
                      // Android tvIssueGiftCard guards: no payout lines in the cart, then no OTD price.
                      const c = cartState();
                      if ((c.lines || []).some((l) => l.isLotteryPayout)) return ToastifyAlert("Remove Lottery Payout", "error");
                      if ((c.lines || []).some((l) => l.isScratcherPayout)) return ToastifyAlert("Remove Scratcher Payout", "error");
                      if (c.isOutdoor) return ToastifyAlert("Remove the OTD price first", "error");
                      setModal("issuegift");
                    })}
                    onLotteryPayout={onLotteryPayout}
                    showLotteryPayout={!!(regSettings && Array.isArray(regSettings.ebtList) && regSettings.ebtList.includes("4"))}
                    onVendorPayout={() => onCashOp("vendor", "MENU_VENDOR_PAYOUT")}
                    onDropCash={() => onCashOp("drop", "MENU_CASH_DROP")}
                    onPayIn={() => onCashOp("payin", "MENU_PAY_IN")}
                    onNoSale={() => {
                      // Android onNoSaleAction (IPF:2652): offline → split → permission → open the drawer only.
                      if (typeof navigator !== "undefined" && navigator.onLine === false) return ToastifyAlert("No Internet", "error");
                      if ((cartState().payments || []).length > 0) return ToastifyAlert("Split payment is in progress", "error");
                      if (!canRegister("MENU_NO_SALE")) return ToastifyAlert("Access Denied!", "error");
                      kickDrawer(regSettings);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="qv-panel qv-grid-panel">
            <MultiplierBar
              value={multiplier}
              customQty={customQty}
              onSelect={(n) => { setCustomQty(null); setMultiplier(n); }}
              onCustom={() => setShowCustomQty(true)}
              onBack={() => { setSearchClear((n) => n + 1); setView("category"); setProducts([]); }} // POS ivBack keeps selectedProductQuantity (its resetQuantity() is commented out)
            />
            <div className="qv-prods-scroll qv-scroll">
              <ProductGrid
                products={products}
                loading={loadingProducts}
                onAdd={(p, v) => addProduct(p, v, multiplier)}
                onLongPress={onLongPress}
              />
            </div>
          </div>
        )}
      </div>

      {/* Cart. On wide screens this is the docked right column; below 900px register.css turns the
          same markup into a bottom sheet driven by `is-open`, so there is ONE cart implementation
          rather than a desktop one and a mobile one that can drift apart. */}
      <div className={cartOpen ? "qv-cart is-open" : "qv-cart"} aria-label="Current sale">
        <CartPanel taxMap={taxMap} onLineTap={openLineEditor} />
        <ActionBar
          onPay={openPayment}
          onCustomer={gated("MENU_ADD_CUSTOMER", () => setModal("customer"))}
          onCancel={() => { setAmountBuffer(""); setMultiplier(1); setCartOpen(false); }}
        />
      </div>
      </div>

      {/* Sheet scrim (small screens only) — tapping outside closes the cart. */}
      <div
        className={cartOpen ? "qv-sheet-scrim is-open" : "qv-sheet-scrim"}
        onClick={() => setCartOpen(false)}
        aria-hidden="true"
      />
      {/* Same scrim for the keypad sheet. The two sheets are mutually exclusive (opening one closes
          the other), so they can share a single z-layer without stacking against each other. */}
      <div
        className={toolsOpen ? "qv-sheet-scrim is-open" : "qv-sheet-scrim"}
        onClick={() => setToolsOpen(false)}
        aria-hidden="true"
      />

      {/* Persistent checkout bar (small screens only). */}
      <CheckoutBar onOpenCart={() => { setToolsOpen(false); setCartOpen(true); }} onPay={() => { setCartOpen(false); setToolsOpen(false); openPayment(); }} />

      {variantProduct && (
        <VariantModal
          product={variantProduct}
          variants={parseVariants(variantProduct)}
          onPick={(v) => {
            dispatch(addItem(buildAddPayload(variantProduct, v, taxMap, 1, mixMatchFor(mixMap, variantProduct, v))));
            setVariantProduct(null);
          }}
          onClose={() => setVariantProduct(null)}
        />
      )}
      {deziWaiting && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 4000, background: "rgba(15,23,42,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <div style={{ background: "#fff", borderRadius: 12, padding: "20px 28px", fontWeight: 500, color: "#0F172A" }}>
            Applying manufacturer discount…
          </div>
        </div>
      )}
      {quickAdd && (
        <QuickAddProductModal
          initialName={quickAdd.name}
          initialUpc={quickAdd.upc}
          taxMap={taxMap}
          onClose={() => setQuickAdd(null)}
          onAdded={async ({ upc }) => {
            // The register reads its catalog from IndexedDB, which only refreshes on the background
            // delta sync — so poll (nudging the sync) until the new product lands, then add it.
            const product = await waitForQuickAddedProduct(upc, { sync: sync.refresh });
            if (!product) {
              ToastifyAlert("Product created — it will appear once the catalog finishes syncing.", "success");
              return;
            }
            addProduct(product, null);
          }}
        />
      )}
      {showPayment && (
        <PaymentMethodModal
          resumeOrderId={resumeOrderId}
          settings={regSettings}
          initialPayAmount={quickPayAmount}
          deziAwait={dezi.awaitSettled}
          deziWaitWarranted={dezi.waitWarranted}
          onComplete={setReceipt}
          onClose={() => { setShowPayment(false); setResumeOrderId(null); }}
        />
      )}
      {receipt && <ReceiptModal model={receipt} onClose={() => setReceipt(null)} />}
      {stockPrompt && (
        <StockPromptModal
          product={stockPrompt.product}
          variant={stockPrompt.variant}
          onUpdated={(newQty) => {
            const p = stockPrompt;
            setStockPrompt(null);
            // Android: the model's quantity is patched in place, then addToCart(model) runs.
            const product = p.variant ? p.product : { ...p.product, quantity: String(newQty) };
            const variant = p.variant ? { ...p.variant, quantity: newQty } : p.variant;
            addProduct(product, variant, p.qty);
          }}
          onClose={() => setStockPrompt(null)}
        />
      )}
      {holdOpen && <HoldBasketModal customer={cartCustomer} onProceed={holdSale} onClose={() => setHoldOpen(false)} />}
      {cashOp && cashOp.kind !== "vendor" && (
        <CashOpModal kind={cashOp.kind} amount={cashOp.amount} onDone={() => setCashOp(null)} onClose={() => setCashOp(null)} />
      )}
      {cashOp && cashOp.kind === "vendor" && <VendorPayoutModal amount={cashOp.amount} onDone={() => setCashOp(null)} onClose={() => setCashOp(null)} />}
      {payoutPick != null && <PayoutKindModal amount={payoutPick} onPick={pickPayout} onClose={() => { setPayoutPick(null); setAmountBuffer(""); }} />}
      {syncConfirm && (
        <ConfirmModal
          title="Sync"
          message="Do you want to sync your data now? This may take a few minutes, so please keep the app open until it finishes."
          okLabel="Yes"
          cancelLabel="No"
          onOk={async () => {
            setSyncConfirm(false);
            setManualSyncing(true);
            try {
              const r = await sync.fullResync();
              if (r && r.status === "busy") ToastifyAlert("Sync is already in progress. Please wait…", "error");
              else ToastifyAlert("Your data has been synced", "success");
            } catch {
              ToastifyAlert("Inventory sync failed. Please try again.", "error");
            } finally {
              setManualSyncing(false);
            }
          }}
          onCancel={() => setSyncConfirm(false)}
          onClose={() => setSyncConfirm(false)}
        />
      )}
      {notFoundPrompt && (
        <ConfirmModal
          title="Product Not Found"
          message="Would you like to add it now?"
          okLabel="Yes"
          cancelLabel="No"
          onOk={() => {
            const p = notFoundPrompt;
            setNotFoundPrompt(null);
            if (canRegister("MENU_QUICKADD")) setQuickAdd(p);
            else ToastifyAlert("Access Denied!", "error");
          }}
          onCancel={() => setNotFoundPrompt(null)}
          onClose={() => setNotFoundPrompt(null)}
        />
      )}
      {customItemFlow && customItemFlow.kind === "ebt" && (
        <ConfirmModal
          title="Custom Item"
          message="Sell Custom Item As Food Stampable"
          onOk={() => { const a = customItemFlow.amount; setCustomItemFlow(null); addCustomLine("Custom Item", a, defaultTax(), { foodStampable: true, isTobacco: !!(regSettings && regSettings.customItemCheckId) }); }}
          onCancel={() => { const a = customItemFlow.amount; setCustomItemFlow(null); addCustomLine("Custom Item", a, defaultTax(), { foodStampable: false, isTobacco: !!(regSettings && regSettings.customItemCheckId) }); }}
          onClose={() => setCustomItemFlow(null)}
        />
      )}
      {customItemFlow && customItemFlow.kind === "tax" && (
        <CustomItemTaxModal
          amount={customItemFlow.amount}
          taxes={[...taxMap.values()].map((t) => ({ id: t.id, title: t.title, displayname: t.displayname || t.displayName || null, percent: t.percent }))}
          showAllowEbt={!!(regSettings && regSettings.allowCustomItemEbt)}
          onDone={({ taxes, allowEbt }) => {
            const a = customItemFlow.amount;
            setCustomItemFlow(null);
            addCustomLine("Custom Item", a, taxes.map((t) => ({ id: t.id, title: t.title, displayName: t.displayname || null, percent: t.percent })), { foodStampable: allowEbt, isTobacco: !!(regSettings && regSettings.customItemCheckId) });
          }}
          onClose={() => setCustomItemFlow(null)}
        />
      )}
      {agePending && (
        <AgeVerifyModal
          settings={regSettings}
          isCustom={!!agePending.custom}
          onVerified={(id) => {
            dispatch(setAgeVerified(id));
            const p = agePending;
            setAgePending(null);
            if (p.custom) addCustomLine(...p.custom); // age-restricted custom item (CUSTOM_ITEM_CHECK_ID)
            else addProduct(p.product, p.variant, p.qty); // now passes the tobacco gate
          }}
          onAddUnverified={() => {
            // showAgeRestrictedForCustomItem "No, Not Age-Restricted" → addToCart without verification
            const [n, price, taxes, opts] = agePending.custom;
            setAgePending(null);
            addCustomLine(n, price, taxes, { ...(opts || {}), isTobacco: false });
          }}
          onCancel={() => setAgePending(null)}
        />
      )}
      {recovery && (
        <RecoveryModal
          order={recovery}
          onResume={async (o) => { await restoreSnapshot(o.snapshot); setResumeOrderId(o.orderId); setShowPayment(true); setRecovery(null); }}
          onDiscarded={() => setRecovery(null)}
        />
      )}
      {modal === "discount" && <CustomDiscountModal onClose={() => setModal(null)} />}
      {modal === "coupon" && <CouponModal onClose={() => setModal(null)} />}
      {modal === "otd" && <OtdModal initialAmount={amountFromBuffer(amountBuffer)} onApplied={() => setAmountBuffer("")} onClose={() => setModal(null)} />}
      {modal === "issuegift" && <IssueGiftCardModal initialAmount={amountFromBuffer(amountBuffer)} onIssued={() => { setAmountBuffer(""); setMultiplier(1); }} onClose={() => setModal(null)} />}
      {modal === "customer" && <CustomerModal onClose={() => setModal(null)} />}
      {lineAction && <LineActionsModal line={lineAction} taxMap={taxMap} onClose={() => setLineAction(null)} />}
      {showHeld && (
        <HeldOrdersModal
          onResume={onResumeHeld}
          onChange={() => setHeldCount(getHeldOrders().length)} // Held (N) badge follows a delete immediately
          onClose={() => { setShowHeld(false); setHeldCount(getHeldOrders().length); }}
        />
      )}
      {showOrders && <OrdersModal onClose={() => setShowOrders(false)} settings={regSettings} />}
      {showPrinters && <PrinterSettingsModal onClose={() => setShowPrinters(false)} />}
      {showCustomQty && <CustomQtyModal onApply={(n) => { setMultiplier(n); setCustomQty(n > 0 ? n : null); }} onClose={() => setShowCustomQty(false)} />}
      {qtyInfo && <QtyInfoModal info={qtyInfo} onClose={() => setQtyInfo(null)} />}
      {/* Live camera scanner — sits at z 1200, UNDER every dialog above, so a scan that opens one
          (variant picker, 21+, Quick Add) is answered on top of the running camera. */}
      {showCamScan && <CameraScanModal onCode={onCameraCode} paused={camPaused} onClose={() => setShowCamScan(false)} />}
    </div>
    </RegThemeContext.Provider>
  );
}
